import React, { useCallback, useEffect, useRef, useState } from "react";
import type { CognitoUser } from "amazon-cognito-identity-js";
import { ArrowLeft, Loader2, Mail, RefreshCw } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { initiateAuth, respondToChallenge, signUp } from "../services/CognitoAuthService";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const OTP_LENGTH = 6;
const RESEND_COOLDOWN_SECONDS = 60;
const CODE_EXPIRY_MINUTES = 10;

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

interface EmailStepProps {
  onSuccess: (email: string, cognitoUser: CognitoUser) => void;
}

function EmailStep({ onSuccess }: EmailStepProps) {
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setError(null);

      const trimmed = email.trim().toLowerCase();
      if (!trimmed || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
        setError("Please enter a valid email address.");
        return;
      }

      setLoading(true);

      // Always sign up first — idempotent (UsernameExistsException is fine).
      // This guarantees the user exists before initiateAuth, which is required
      // because preventUserExistenceErrors: true means Cognito never returns
      // USER_NOT_FOUND — it would crash the Lambda instead.
      const signUpResult = await signUp(trimmed);
      if (!signUpResult.ok && signUpResult.error.type !== "USER_EXISTS") {
        setLoading(false);
        setError("Something went wrong. Please try again.");
        return;
      }

      // User is guaranteed to exist now — initiate auth
      const result = await initiateAuth(trimmed);

      setLoading(false);

      if (!result.ok) {
        if (result.error.type === "TOO_MANY_REQUESTS") {
          setError("Too many attempts. Please wait a moment and try again.");
        } else {
          setError("Something went wrong. Please try again.");
        }
        return;
      }

      onSuccess(trimmed, result.cognitoUser);
    },
    [email, onSuccess],
  );

  return (
    <form onSubmit={handleSubmit} noValidate className="space-y-5">
      <div>
        <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1.5">
          Email address
        </label>
        <input
          id="email"
          type="email"
          autoComplete="email"
          autoFocus
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            if (error) setError(null);
          }}
          placeholder="you@example.com"
          className={[
            "w-full px-4 py-3 rounded-xl border text-base transition-colors",
            "placeholder:text-gray-400 outline-none",
            error
              ? "border-red-400 bg-red-50 focus:border-red-500 focus:ring-2 focus:ring-red-200"
              : "border-gray-300 bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-100",
          ].join(" ")}
        />
        {error && (
          <p className="mt-1.5 text-sm text-red-600" role="alert">
            {error}
          </p>
        )}
      </div>

      <button
        type="submit"
        disabled={loading || email.trim() === ""}
        className={[
          "w-full py-3 px-4 rounded-xl text-base font-semibold transition-all",
          "flex items-center justify-center gap-2",
          loading || email.trim() === ""
            ? "bg-gray-200 text-gray-400 cursor-not-allowed"
            : "bg-blue-600 text-white hover:bg-blue-700 active:bg-blue-800 shadow-sm hover:shadow",
        ].join(" ")}
      >
        {loading ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" />
            Sending code…
          </>
        ) : (
          "Continue"
        )}
      </button>

      <p className="text-xs text-center text-gray-500">
        We'll send a one-time code to your email. No password needed.
      </p>
    </form>
  );
}

// ---------------------------------------------------------------------------

interface OtpInputProps {
  values: string[];
  onChange: (values: string[]) => void;
  hasError: boolean;
  disabled: boolean;
}

function OtpInput({ values, onChange, hasError, disabled }: OtpInputProps) {
  const refs = useRef<(HTMLInputElement | null)[]>([]);

  const focusAt = useCallback((index: number) => {
    refs.current[index]?.focus();
  }, []);

  // Auto-focus first input on mount
  useEffect(() => {
    refs.current[0]?.focus();
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
      if (e.key === "Backspace") {
        if (values[index] !== "") {
          const next = [...values];
          next[index] = "";
          onChange(next);
        } else if (index > 0) {
          focusAt(index - 1);
        }
      } else if (e.key === "ArrowLeft" && index > 0) {
        focusAt(index - 1);
      } else if (e.key === "ArrowRight" && index < OTP_LENGTH - 1) {
        focusAt(index + 1);
      }
    },
    [values, onChange, focusAt],
  );

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
      const raw = e.target.value.replace(/\D/g, "");
      if (!raw) return;

      // Handle paste of multiple digits
      if (raw.length > 1) {
        const digits = raw.slice(0, OTP_LENGTH).split("");
        const next = [...values];
        digits.forEach((d, i) => {
          if (index + i < OTP_LENGTH) next[index + i] = d;
        });
        onChange(next);
        const nextFocus = Math.min(index + digits.length, OTP_LENGTH - 1);
        focusAt(nextFocus);
        return;
      }

      const next = [...values];
      next[index] = raw;
      onChange(next);
      if (index < OTP_LENGTH - 1) {
        focusAt(index + 1);
      }
    },
    [values, onChange, focusAt],
  );

  const handlePaste = useCallback(
    (e: React.ClipboardEvent<HTMLInputElement>, index: number) => {
      e.preventDefault();
      const text = e.clipboardData.getData("text").replace(/\D/g, "");
      if (!text) return;

      const digits = text.slice(0, OTP_LENGTH).split("");
      const next = [...values];
      digits.forEach((d, i) => {
        if (index + i < OTP_LENGTH) next[index + i] = d;
      });
      onChange(next);
      const nextFocus = Math.min(index + digits.length - 1, OTP_LENGTH - 1);
      focusAt(nextFocus);
    },
    [values, onChange, focusAt],
  );

  return (
    <div className="flex gap-2 justify-center" role="group" aria-label="One-time password">
      {values.map((digit, i) => (
        <input
          key={i}
          ref={(el) => {
            refs.current[i] = el;
          }}
          type="text"
          inputMode="numeric"
          pattern="[0-9]"
          maxLength={6}
          value={digit}
          disabled={disabled}
          aria-label={`Digit ${i + 1}`}
          onChange={(e) => handleChange(e, i)}
          onKeyDown={(e) => handleKeyDown(e, i)}
          onPaste={(e) => handlePaste(e, i)}
          onFocus={(e) => e.target.select()}
          className={[
            "w-11 h-14 text-center text-xl font-semibold rounded-xl border-2 transition-all outline-none",
            "disabled:opacity-50 disabled:cursor-not-allowed",
            hasError
              ? "border-red-400 bg-red-50 text-red-700 focus:border-red-500 focus:ring-2 focus:ring-red-200"
              : digit
                ? "border-blue-400 bg-blue-50 text-blue-700 focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                : "border-gray-300 bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-100",
          ].join(" ")}
        />
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------

interface OtpStepProps {
  email: string;
  cognitoUser: CognitoUser;
  onBack: () => void;
  onSuccess: (returnUrl: string) => void;
  returnUrl: string;
}

function OtpStep({ email, cognitoUser, onBack, onSuccess, returnUrl }: OtpStepProps) {
  const { loginAsCandidate } = useAuth();
  const [digits, setDigits] = useState<string[]>(Array(OTP_LENGTH).fill(""));
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [shake, setShake] = useState(false);

  // Resend cooldown
  const [resendCooldown, setResendCooldown] = useState(RESEND_COOLDOWN_SECONDS);
  const [resending, setResending] = useState(false);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setInterval(() => {
      setResendCooldown((c) => Math.max(0, c - 1));
    }, 1000);
    return () => clearInterval(timer);
  }, [resendCooldown]);

  const code = digits.join("");
  const isComplete = code.length === OTP_LENGTH;

  const triggerShake = useCallback(() => {
    setShake(true);
    setTimeout(() => setShake(false), 600);
  }, []);

  const handleSubmit = useCallback(
    async (e?: React.FormEvent) => {
      e?.preventDefault();
      if (!isComplete || loading) return;

      setError(null);
      setLoading(true);

      const result = await respondToChallenge(cognitoUser, code);
      setLoading(false);

      if (!result.ok) {
        if (result.error.type === "EXPIRED_CODE") {
          setError("This code has expired. Please request a new one.");
        } else if (result.error.type === "NOT_AUTHORIZED") {
          setError("Too many attempts. Please go back and start over.");
        } else {
          setError("Incorrect code. Please try again.");
        }
        setDigits(Array(OTP_LENGTH).fill(""));
        triggerShake();
        return;
      }

      loginAsCandidate(result.tokens);
      onSuccess(returnUrl);
    },
    [isComplete, loading, cognitoUser, code, loginAsCandidate, onSuccess, returnUrl, triggerShake],
  );

  // Auto-submit when all digits filled
  useEffect(() => {
    if (isComplete && !loading && !error) {
      void handleSubmit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isComplete]);

  const handleResend = useCallback(async () => {
    if (resendCooldown > 0 || resending) return;
    setResending(true);
    setError(null);
    const result = await initiateAuth(email);
    setResending(false);
    if (result.ok) {
      setResendCooldown(RESEND_COOLDOWN_SECONDS);
      setDigits(Array(OTP_LENGTH).fill(""));
    } else {
      setError("Failed to resend code. Please try again.");
    }
  }, [email, resendCooldown, resending]);

  return (
    <form onSubmit={handleSubmit} noValidate className="space-y-6">
      <div className={shake ? "animate-shake" : ""}>
        <OtpInput
          values={digits}
          onChange={(next) => {
            setDigits(next);
            if (error) setError(null);
          }}
          hasError={!!error}
          disabled={loading}
        />
      </div>

      {error && (
        <p className="text-sm text-center text-red-600" role="alert">
          {error}
        </p>
      )}

      <button
        type="submit"
        disabled={!isComplete || loading}
        className={[
          "w-full py-3 px-4 rounded-xl text-base font-semibold transition-all",
          "flex items-center justify-center gap-2",
          !isComplete || loading
            ? "bg-gray-200 text-gray-400 cursor-not-allowed"
            : "bg-blue-600 text-white hover:bg-blue-700 active:bg-blue-800 shadow-sm hover:shadow",
        ].join(" ")}
      >
        {loading ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" />
            Verifying…
          </>
        ) : (
          "Verify & Continue"
        )}
      </button>

      <div className="flex flex-col items-center gap-1">
        <button
          type="button"
          onClick={handleResend}
          disabled={resendCooldown > 0 || resending}
          className={[
            "flex items-center gap-1.5 text-sm transition-colors",
            resendCooldown > 0 || resending
              ? "text-gray-400 cursor-not-allowed"
              : "text-blue-600 hover:text-blue-700",
          ].join(" ")}
        >
          {resending ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : (
            <RefreshCw className="w-3.5 h-3.5" />
          )}
          {resendCooldown > 0
            ? `Resend code in ${resendCooldown}s`
            : resending
              ? "Sending…"
              : "Resend code"}
        </button>
        <p className="text-xs text-gray-400">Code expires in {CODE_EXPIRY_MINUTES} minutes</p>
      </div>

      <button
        type="button"
        onClick={onBack}
        className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 transition-colors mx-auto"
      >
        <ArrowLeft className="w-4 h-4" />
        Use a different email
      </button>
    </form>
  );
}

// ---------------------------------------------------------------------------
// CandidateLoginPage
// ---------------------------------------------------------------------------

export function CandidateLoginPage() {
  type Step = "email" | "otp";
  const [step, setStep] = useState<Step>("email");
  const [email, setEmail] = useState("");
  const [cognitoUser, setCognitoUser] = useState<CognitoUser | null>(null);

  // Extract return URL from query string
  const returnUrl = new URLSearchParams(window.location.search).get("returnUrl") ?? "/my-applications";

  const handleEmailSuccess = useCallback((resolvedEmail: string, user: CognitoUser) => {
    setEmail(resolvedEmail);
    setCognitoUser(user);
    setStep("otp");
  }, []);

  const handleOtpSuccess = useCallback((url: string) => {
    window.location.href = url;
  }, []);

  const handleBack = useCallback(() => {
    setStep("email");
    setCognitoUser(null);
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="w-full max-w-sm">
        {/* Card */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8">
          {/* Header */}
          <div className="mb-8 text-center">
            <div className="inline-flex items-center justify-center w-12 h-12 bg-blue-100 rounded-xl mb-4">
              <Mail className="w-6 h-6 text-blue-600" />
            </div>
            {step === "email" ? (
              <>
                <h1 className="text-xl font-bold text-gray-900">Sign in to apply</h1>
                <p className="mt-1 text-sm text-gray-500">
                  Enter your email and we'll send you a code
                </p>
              </>
            ) : (
              <>
                <h1 className="text-xl font-bold text-gray-900">Check your email</h1>
                <p className="mt-1 text-sm text-gray-500">
                  We sent a 6-digit code to
                </p>
                <p className="text-sm font-medium text-gray-800 truncate">{email}</p>
              </>
            )}
          </div>

          {/* Step content */}
          {step === "email" && (
            <EmailStep onSuccess={handleEmailSuccess} />
          )}

          {step === "otp" && cognitoUser && (
            <OtpStep
              email={email}
              cognitoUser={cognitoUser}
              onBack={handleBack}
              onSuccess={handleOtpSuccess}
              returnUrl={returnUrl}
            />
          )}
        </div>

        {/* Footer */}
        <p className="mt-6 text-center text-xs text-gray-400">
          Your information is kept private and secure.
        </p>

        {/* Switch to manager login */}
        <p className="mt-3 text-center text-sm text-gray-500">
          Are you a hiring manager?{" "}
          <a href="/login/manager" className="text-blue-600 hover:text-blue-500 font-medium">
            Sign in as a manager
          </a>
        </p>
      </div>
    </div>
  );
}

export default CandidateLoginPage;
